/**
 * @file DigitalProductFilePreview.tsx
 *
 * Lists the downloadable files attached to a digital product as removable cards,
 * with loader placeholders for in-flight uploads. Pure presentation: the file
 * list is owned by the form's media hook and updated through `onFilesChange`.
 * The upload target itself is the shared {@link MediaDropZone}, rendered by the
 * form alongside this preview.
 */

import { Icon, Loader } from '@group.one/react-gravity';
import { useIntl } from 'react-intl';
import { adminMessages } from '../../../i18n/messages';
import type { DownloadableFile } from '../product-form-types';
import styles from '../ProductForm.module.css';

interface DigitalProductFilePreviewProps {
  files: DownloadableFile[];
  onFilesChange: (files: DownloadableFile[]) => void;
  uploadingCount: number;
}

/**
 * Renders the downloadable-files preview grid.
 *
 * @param props - The files, a change handler, and the in-flight upload count.
 *
 * @returns The file-preview element, or `null` when there is nothing to show.
 */
export function DigitalProductFilePreview({
  files,
  onFilesChange,
  uploadingCount,
}: DigitalProductFilePreviewProps) {
  const intl = useIntl();

  if (files.length === 0 && uploadingCount === 0) {
    return null;
  }

  const remove = (id: string): void => {
    onFilesChange(files.filter((file) => file.id !== id));
  };

  return (
    <div className={styles.fileGrid}>
      {files.map((file) => (
        <div key={file.id} className={styles.fileCard}>
          <Icon name="description" size="medium" />
          <span className={`gv-text-sm ${styles.fileName}`} title={file.name}>
            {file.name}
          </span>
          <button
            type="button"
            className="gv-button gv-button-bare"
            aria-label={intl.formatMessage(adminMessages.formRemoveFile)}
            onClick={() => remove(file.id)}
          >
            <Icon name="close" size="small" />
          </button>
        </div>
      ))}
      {Array.from({ length: uploadingCount }).map((_, i) => (
        <div key={`uploading-${i}`} className={styles.fileCard}>
          <Loader condensed>{intl.formatMessage(adminMessages.formUploading)}</Loader>
        </div>
      ))}
    </div>
  );
}
